import React, { useEffect, useState } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'saving';

export interface ToastMessage {
  id: string;
  msg: string;
  type: ToastType;
  duration?: number;
}

const listeners: Array<(t: ToastMessage) => void> = [];

export function showToast(msg: string, type: ToastType = 'success', duration = 4000) {
  const t: ToastMessage = { id: Date.now().toString(), msg, type, duration };
  listeners.forEach(fn => fn(t));
}
export function showSaving(msg = 'Saving...') { showToast(msg, 'saving', 99999); }
export function showSaved(msg = 'Saved successfully') { showToast(msg, 'success', 3500); }
export function showError(msg: string) { showToast(msg, 'error', 6000); }
export function showInfo(msg: string) { showToast(msg, 'info', 4000); }
export function showWarning(msg: string) { showToast(msg, 'warning', 5000); }

const ICONS: Record<ToastType, string> = {
  success: '✅', error: '❌', info: 'ℹ️', warning: '⚠️', saving: '⏳',
};
const COLORS: Record<ToastType, { bg: string; border: string; text: string }> = {
  success: { bg: '#0A2E1A', border: '#22C55E', text: '#86EFAC' },
  error:   { bg: '#2E0A0A', border: '#EF4444', text: '#FCA5A5' },
  info:    { bg: '#0A1A2E', border: '#3B82F6', text: '#93C5FD' },
  warning: { bg: '#2E1F0A', border: '#F59E0B', text: '#FCD34D' },
  saving:  { bg: '#1A1A0A', border: '#C9A84C', text: '#FDE68A' },
};

export function GlobalToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const handler = (t: ToastMessage) => {
      setToasts(prev => {
        const filtered = t.type !== 'saving' ? prev.filter(p => p.type !== 'saving') : prev;
        return [...filtered, t];
      });
      if (t.duration && t.duration < 90000) {
        setTimeout(() => setToasts(prev => prev.filter(p => p.id !== t.id)), t.duration);
      }
    };
    listeners.push(handler);
    return () => { const idx = listeners.indexOf(handler); if (idx > -1) listeners.splice(idx, 1); };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 99999,
      display: 'flex', flexDirection: 'column', gap: 10,
      maxWidth: 400, pointerEvents: 'none',
    }}>
      {toasts.map(t => {
        const c = COLORS[t.type];
        return (
          <div key={t.id} style={{
            background: c.bg, border: `1px solid ${c.border}`,
            borderRadius: 10, padding: '13px 18px',
            display: 'flex', alignItems: 'flex-start', gap: 10,
            boxShadow: `0 4px 24px rgba(0,0,0,0.5), 0 0 0 1px ${c.border}22`,
            animation: 'gtSlideIn 0.25s ease-out', pointerEvents: 'auto',
          }}>
            <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{ICONS[t.type]}</span>
            <span style={{ color: c.text, fontSize: 14, lineHeight: 1.5,
              fontFamily: 'system-ui, sans-serif', flex: 1 }}>{t.msg}</span>
          </div>
        );
      })}
      <style>{`
        @keyframes gtSlideIn { from { transform:translateX(110%); opacity:0; } to { transform:translateX(0); opacity:1; } }
      `}</style>
    </div>
  );
}
